class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

// Solo puede haber un crossfade entre megamenús en curso a la vez. Si el
// usuario clickea un tercer menú mientras el anterior se está desvaneciendo,
// el swap pendiente se cancela: si no, corre igual unos ms después y cierra el
// menú que se acaba de abrir.
let pendingMegaMenuCrossfade = null;

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.isBigMenu = this.mainDetailsToggle.classList.contains('mega-menu--big');
    this.closeTimer = null;
    this.carousel = null;
    this.carouselToggle = null;
    this.announcementCollapsedClass = 'announcement-bar-section--collapsed';
    this.announcementTransitionFallback = 450;
    // Deben coincidir con las duraciones del crossfade en
    // component-mega-menu.css (.mega-menu__content--fade-out / --fade-in).
    this.crossfadeOutMs = 180;
    this.crossfadeInMs = 250;

    if (this.isBigMenu) {
      this.setupBigMenuInteraction();
      this.setupCarouselToggle();
      this.setupCloseButton();
    }
  }

  // Los megamenús grandes solo se cierran con el botón de close explícito,
  // un click en su propio trigger, o al abrir otro menú — nunca al perder
  // el foco (pedido del usuario). Esto además elimina una condición de
  // carrera real: el setTimeout de focusout (heredado de DetailsDisclosure)
  // a veces corría ANTES de que terminara de procesarse el click en OTRO
  // menú, cerrando este y restaurando de paso la barra de anuncios a mitad
  // de camino — eso es lo que se sentía como un cierre completo
  // intermitente al cambiar entre menús.
  onFocusOut() {
    if (this.isBigMenu) return;
    super.onFocusOut();
  }

  setupCloseButton() {
    this.closeButton = this.mainDetailsToggle.querySelector('[data-mega-menu-close]');
    if (!this.closeButton) return;

    this.closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.clearCloseTimer();
      this.close();
    });
  }

  // El trigger es por click (no por hover): al abrir, primero se esconde la
  // barra de anuncios con una animación; recién cuando esa animación termina
  // se marca el <details> como abierto, lo que dispara la aparición
  // escalonada del contenido (megaBigItemIn, en component-mega-menu.css).
  setupBigMenuInteraction() {
    this.summary = this.mainDetailsToggle.querySelector('summary');
    if (!this.summary) return;

    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      this.handleSummaryClick();
    });
  }

  handleSummaryClick() {
    // Si este mismo menú ya está abierto, un click sobre su propio trigger
    // lo cierra (toggle), igual que el botón de close explícito.
    if (this.mainDetailsToggle.open) {
      this.clearCloseTimer();
      this.close();
      return;
    }

    this.clearCloseTimer();

    const otherOpen = this.findOtherOpenBigMenu();
    if (otherOpen) {
      this.crossfadeFrom(otherOpen);
      return;
    }

    this.openAfterAnnouncementCollapse(false);
  }

  findOtherOpenBigMenu() {
    const openMenus = document.querySelectorAll('.mega-menu--big[open]');
    for (let i = 0; i < openMenus.length; i++) {
      if (openMenus[i] !== this.mainDetailsToggle) return openMenus[i];
    }
    return null;
  }

  // Cambio entre megamenús (ej. Hombre -> Mujer): primero se desvanece el
  // contenido del que estaba abierto y recién cuando termina se muestra el
  // nuevo, también con fade. Solo se anima el contenido interno; el panel se
  // intercambia de golpe (ver la nota del crossfade en component-mega-menu.css).
  crossfadeFrom(otherDetails) {
    const otherContent = otherDetails.querySelector('.mega-menu__content--big');
    const otherLayout = otherContent ? otherContent.querySelector('.mega-menu__layout') : null;

    if (!otherContent || !otherLayout) {
      this.closeOtherBigMenus();
      this.openAfterAnnouncementCollapse(true);
      return;
    }

    this.cancelPendingCrossfade();

    let swapped = false;
    let fallbackTimer = null;

    const swap = () => {
      if (swapped) return;
      swapped = true;
      clearTimeout(fallbackTimer);
      otherContent.removeEventListener('transitionend', onFadeOutEnd);
      otherContent.classList.remove('mega-menu__content--fade-out');
      pendingMegaMenuCrossfade = null;
      this.closeOtherBigMenus();
      this.openAfterAnnouncementCollapse(true, true);
    };

    const onFadeOutEnd = (event) => {
      if (event.target !== otherLayout || event.propertyName !== 'opacity') return;
      swap();
    };

    otherContent.addEventListener('transitionend', onFadeOutEnd);
    // Red de seguridad: si el transitionend no llega (transición cancelada,
    // prefers-reduced-motion, etc.) el cambio se hace igual.
    fallbackTimer = setTimeout(swap, this.crossfadeOutMs + 60);
    otherContent.classList.add('mega-menu__content--fade-out');

    pendingMegaMenuCrossfade = () => {
      swapped = true;
      clearTimeout(fallbackTimer);
      otherContent.removeEventListener('transitionend', onFadeOutEnd);
    };
  }

  cancelPendingCrossfade() {
    if (!pendingMegaMenuCrossfade) return;
    pendingMegaMenuCrossfade();
    pendingMegaMenuCrossfade = null;
  }

  // Cierra cualquier otro megamenú grande que haya quedado abierto y
  // devuelve true si encontró alguno (estábamos "cambiando" de uno a otro,
  // no abriendo desde cero). Cada item del nav tiene su propio <details> con
  // su propio contenedor/fondo/blur, así que sin esto el viejo jugaba su
  // transición normal de salida (fade + translateY) mientras el nuevo
  // aparecía instantáneo encima: eso es lo que se veía como "se cierra y
  // se vuelve a abrir el overlay". Al anular su transición antes de
  // remover el atributo open, el viejo desaparece en el mismo frame en que
  // el nuevo aparece, sin fade visible.
  closeOtherBigMenus() {
    let closedAny = false;
    document.querySelectorAll('.mega-menu--big[open]').forEach((details) => {
      if (details === this.mainDetailsToggle) return;

      const content = details.querySelector('.mega-menu__content--big');
      if (content) content.classList.add('mega-menu__content--no-transition');

      details.removeAttribute('open');
      details.classList.remove('mega-menu--switching');
      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', false);
      closedAny = true;

      if (content) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            content.classList.remove('mega-menu__content--no-transition');
          });
        });
      }
    });
    return closedAny;
  }

  openAfterAnnouncementCollapse(instant, fadeIn) {
    const openNow = () => {
      const content = instant ? this.mainDetailsToggle.querySelector('.mega-menu__content--big') : null;
      if (content) content.classList.add('mega-menu__content--no-transition');
      if (content && fadeIn) content.classList.add('mega-menu__content--fade-in');
      if (instant) this.mainDetailsToggle.classList.add('mega-menu--switching');

      this.mainDetailsToggle.setAttribute('open', '');
      if (this.summary) this.summary.setAttribute('aria-expanded', true);

      if (content) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            content.classList.remove('mega-menu__content--no-transition');
          });
        });
      }

      // La clase del fade-in se saca al terminar para que la animación pueda
      // volver a correr en el próximo cambio de menú.
      if (content && fadeIn) {
        setTimeout(() => {
          content.classList.remove('mega-menu__content--fade-in');
        }, this.crossfadeInMs + 60);
      }
    };

    if (instant) {
      openNow();
      return;
    }

    const bar = document.querySelector('.utility-bar');
    if (!bar || bar.classList.contains(this.announcementCollapsedClass)) {
      openNow();
      return;
    }

    bar.classList.add(this.announcementCollapsedClass);

    let opened = false;
    const onDone = (event) => {
      if (event && event.target !== bar) return;
      if (opened) return;
      opened = true;
      bar.removeEventListener('transitionend', onDone);
      openNow();
    };

    bar.addEventListener('transitionend', onDone);
    setTimeout(onDone, this.announcementTransitionFallback);
  }

  maybeRestoreAnnouncementBar() {
    if (document.querySelector('.mega-menu--big[open]')) return;
    const bar = document.querySelector('.utility-bar');
    if (bar) bar.classList.remove(this.announcementCollapsedClass);
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  setupCarouselToggle() {
    this.carousel = this.mainDetailsToggle.querySelector('[data-carousel]');
    this.carouselToggle = this.mainDetailsToggle.querySelector('[data-carousel-toggle]');
    // Las colecciones que no entran en la primera fila vienen agrupadas en
    // tandas de 6, cada una en su propio contenedor colapsable (ver
    // header-big-megamenu.liquid).
    this.carouselBatches = Array.from(this.mainDetailsToggle.querySelectorAll('[data-carousel-more]'));

    if (!this.carouselToggle) return;

    this.carouselToggle.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.clearCloseTimer();
      this.toggleCarouselExpanded();
    });
  }

  // Cada click abre la siguiente tanda de 6 colecciones. Cuando ya están todas
  // a la vista el botón pasa a decir "Ver menos" y el siguiente click las
  // colapsa de nuevo.
  toggleCarouselExpanded() {
    const batches = this.carouselBatches || [];
    const nextBatch = batches.find((batch) => !batch.classList.contains('is-expanded'));

    if (nextBatch) {
      nextBatch.classList.add('is-expanded');
    } else {
      batches.forEach((batch) => batch.classList.remove('is-expanded'));
    }

    const isExpanded = batches.some((batch) => batch.classList.contains('is-expanded'));
    const allExpanded = isExpanded && batches.every((batch) => batch.classList.contains('is-expanded'));

    this.mainDetailsToggle.classList.toggle('is-carousel-expanded', isExpanded);
    this.carousel?.classList.toggle('is-expanded', isExpanded);
    this.carouselToggle.setAttribute('aria-expanded', String(isExpanded));

    const labelExpanded = this.carouselToggle.dataset.labelExpanded;
    const labelCollapsed = this.carouselToggle.dataset.labelCollapsed;
    this.carouselToggle.textContent = allExpanded ? labelCollapsed : labelExpanded;
  }

  resetCarouselExpanded() {
    this.mainDetailsToggle.classList.remove('is-carousel-expanded');
    this.carousel?.classList.remove('is-expanded');
    (this.carouselBatches || []).forEach((batch) => batch.classList.remove('is-expanded'));

    if (!this.carouselToggle) return;

    this.carouselToggle.setAttribute('aria-expanded', 'false');
    this.carouselToggle.textContent = this.carouselToggle.dataset.labelExpanded;
  }

  close() {
    this.cancelPendingCrossfade();
    this.resetCarouselExpanded();
    this.mainDetailsToggle.classList.remove('mega-menu--switching');
    const content = this.mainDetailsToggle.querySelector('.mega-menu__content--big');
    if (content) {
      content.classList.remove('mega-menu__content--fade-out', 'mega-menu__content--fade-in');
    }
    super.close();
    if (this.isBigMenu) this.maybeRestoreAnnouncementBar();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (!this.mainDetailsToggle.open) {
      this.resetCarouselExpanded();
    }

    // Clase usada por el CSS de la barra tipo píldora para invertir textos
    // e íconos a negro mientras el megamenú grande está abierto (ver
    // header-wrapper--invert-menu-colors en header.liquid). Como
    // closeOtherBigMenus() cierra cualquier otro menú grande (disparando su
    // propio toggle) antes de que este se abra, a lo sumo un menú grande
    // tiene la clase puesta a la vez.
    if (this.isBigMenu) {
      this.header.classList.toggle('header-wrapper--big-menu-open', this.mainDetailsToggle.open);
    }

    if (this.mainDetailsToggle.open && this.isBigMenu) {
      this.updateBridgeHeight();
      // Si es un cambio instantáneo entre menús (mega-menu--switching, ver
      // openAfterAnnouncementCollapse), NO se debe reiniciar la animación
      // escalonada de los items: por eso el contenido se refresca sin que
      // el panel se sienta como cerrado y vuelto a abrir.
      if (!this.mainDetailsToggle.classList.contains('mega-menu--switching')) {
        this.restartBigMenuAnimation();
      }
    }

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '' && !this.isBigMenu) return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  // Calcula el alto del "puente" invisible entre el item del menu y el borde
  // inferior del header para que el hover no se pierda al navegar hacia abajo.
  updateBridgeHeight() {
    if (!this.summary || !this.header) return;
    const headerBottom = this.header.getBoundingClientRect().bottom;
    const summaryBottom = this.summary.getBoundingClientRect().bottom;
    const gap = Math.max(0, Math.floor(headerBottom - summaryBottom));
    this.mainDetailsToggle.style.setProperty('--mega-bridge-height', `${gap}px`);
  }

  restartBigMenuAnimation() {
    const items = this.mainDetailsToggle.querySelectorAll(
      '.mega-menu__list--big > li, .mega-menu__carousel-list--primary > .mega-menu__carousel-item, .mega-menu__carousel-button'
    );
    if (!items.length) return;

    items.forEach((item) => {
      item.style.animation = 'none';
    });

    // Forzar reflow para que la animacion se reinicie en cada apertura
    void this.mainDetailsToggle.offsetWidth;

    items.forEach((item) => {
      item.style.animation = '';
    });
  }
}

customElements.define('header-menu', HeaderMenu);
